/**
 * Karr Travel's own addresses (ts-2, docs/KARR_TRAVEL_SCALE_SPEC.md section 12).
 *
 * travel.karr.pro and every brand's own domain (holidays.example.com,
 * agents.example.com) serve the same bundle as karr.pro. There the root is the
 * travel company's front door, never the accounting site. Karr's own hosts
 * (karr.pro and its subdomains, a local dev server, a Pages preview) are never
 * a brand's: a brand domain reaches Karr only when the founder adds it to Pages
 * as a custom domain for that brand (spec 15).
 *
 * Tiny on purpose: the router reads it on the first paint.
 */

const KARR_HOST = /(^|\.)karr\.pro$|(^|\.)pages\.dev$|(^|\.)workers\.dev$|^localhost$|^127\.0\.0\.1$|^\[?::1\]?$|\.local$|\.localhost$/i;

/** travel.karr.pro (or travel.localhost): Karr Travel's own address. */
export function isTravelSubdomain(hostname: string): boolean {
    return /^travel\./i.test(hostname);
}

/** Hosts that are never a brand's domain: an IP address, a name with no dot, a translation proxy. */
const NOT_A_BRAND = /^\d{1,3}(\.\d{1,3}){3}(:\d+)?$|^\[?[0-9a-f:]+\]?$|^[^.]+$|\.translate\.goog$|\.googleusercontent\.com$/i;

/** A brand's own domain: a real domain that is not one of Karr's. */
export function isBrandHost(hostname: string): boolean {
    const h = hostname.trim().toLowerCase();
    return h !== '' && !KARR_HOST.test(h) && !NOT_A_BRAND.test(h) && !/^health\./.test(h);
}

/** ts-24: a brand's agent site, agents.<brand domain>: its root is the agent sign-in, never the staff desk. */
export function isAgentHost(hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''): boolean {
    return isBrandHost(hostname) && /^agents\./i.test(hostname.trim());
}

/** Is this page served from a travel address (travel.karr.pro or a brand's domain)? */
export function isTravelHost(hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''): boolean {
    return isTravelSubdomain(hostname) || isBrandHost(hostname);
}

/** Is this sign-in a travel staff login (an employee number and a PIN, made by staff-auth)? */
export function isTravelLogin(user: { app_metadata?: Record<string, unknown> } | null | undefined): boolean {
    const t = user?.app_metadata?.travel as { org_id?: string } | undefined;
    return !!t?.org_id;
}
